/**
 * Where a sound sits between the ears. PURE, so `pan.test.ts` can pin the yaw
 * convention — a mirrored stereo image is fatal and invisible in review. Yaw 0
 * looks down −Z, +yaw counter-clockwise: forward = (−sin yaw, −cos yaw), right
 * = (cos yaw, −sin yaw), the pair `movement.ts` strafes along. At yaw 0 the
 * right ear is at +X.
 */

/** A source closer than this is ON the listener and gets no pan at all. */
const NEAR_M = 0.5;

/** Anything with an x and a z. Deliberately not `Vec3` — y is never read. */
export interface PanPoint {
  x: number;
  z: number;
}

/**
 * Stereo pan, −1 to +1, for `src` heard from `listener` facing `listenerYaw`.
 * Projected onto the RIGHT vector only — dead ahead and dead behind both give
 * 0; front/back is carried by the distance filtering. Non-finite lands on 0,
 * since a NaN pan throws.
 */
export function panFor(listener: PanPoint, listenerYaw: number, src: PanPoint): number {
  const dx = src.x - listener.x;
  const dz = src.z - listener.z;
  if (!Number.isFinite(dx) || !Number.isFinite(dz) || !Number.isFinite(listenerYaw)) {
    return 0;
  }

  const dist = Math.sqrt(dx * dx + dz * dz);
  // On top of the listener the direction is meaningless — a footstep under your
  // own feet hard-panned is the tell.
  if (dist < NEAR_M) return 0;

  const rightX = Math.cos(listenerYaw);
  const rightZ = -Math.sin(listenerYaw);
  const pan = (dx * rightX + dz * rightZ) / dist;
  // `dist` is the length of (dx, dz) and `right` is a unit vector, so this is a
  // cosine and already in range; the clamp is against float drift only.
  return pan < -1 ? -1 : pan > 1 ? 1 : pan;
}
